///
/// EarthBrowser
///
/// JetStreamSphereNode - the 250 hPa jet stream shell
///
/// Draws the jet stream as animated flow streaks (jetstream_sphere.glsl) on
/// its own sphere above the surface analysis, so a tilted camera sees the
/// vertical structure: surface lows beneath the jet rather than painted on
/// the same plane. The shell's altitude is exaggerated with camera
/// distance (shellAltitude.ts) and relaxes to the true height as the
/// camera descends.
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet, Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type matrix } from "@stovokor/math/matrix"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { cJetComponentRangeMps, cJetSpeedRangeMps } from "../weather-grid/JetStreamFieldGenerator"
import { type JetStreamComputedFrame } from "./JetStreamComputedFrame"
import { PressureFieldTextureSeries } from "./PressureFieldTextureSeries"
import { PressureIsolineSphereGeometry } from "./PressureIsolineSphereGeometry"
import { cJetShell, shellAltitudeKm } from "./shellAltitude"

// Camera altitudes between which the shell fades out on descent
const cFadeOutAltitudeKm = 500
const cFadeInAltitudeKm = 1200

class JetStreamSphereMaterial implements Material {
	public available: boolean = true
	public transparency: boolean = true
}

class JetStreamSphereRenderer extends Renderer {
	private modelViewProjection = new UniformDynamic<matrix>()
	private sphereRadius = new UniformDynamic<number>()
	private texture0 = new UniformDynamic<Texture>()
	private texture1 = new UniformDynamic<Texture>()
	private blendFactor = new UniformDynamic<number>()
	private fadeFactor = new UniformDynamic<number>()
	private flowTime = new UniformDynamic<number>()
	private componentRange = new UniformDynamic<number>()
	private speedRange = new UniformDynamic<number>()
	/** Shell altitude used for the last frame (km above the ellipsoid) */
	public currentAltitudeKm = cJetShell.trueKm

	constructor(context: EarthBrowserContext, geometry: PressureIsolineSphereGeometry, material: Material) {
		const source = new ShaderSourceDefinition("jetstream_sphere")
		super(context, source, geometry, [material])

		this.drawState.cullFace = false
		this.drawState.depthWrite = false

		this.sphereRadius.value = geometry.sphereRadius
		this.blendFactor.value = 0
		this.fadeFactor.value = 1
		this.flowTime.value = 0
		this.componentRange.value = cJetComponentRangeMps
		this.speedRange.value = cJetSpeedRangeMps
	}

	public updateTextures(tex0: Texture, tex1: Texture, blend: number, fade: number): void {
		this.texture0.value = tex0
		this.texture1.value = tex1
		this.blendFactor.value = blend
		this.fadeFactor.value = fade
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
		const cameraAltitudeKm = new geographic(state.camera.position).altitude
		this.currentAltitudeKm = shellAltitudeKm(cJetShell, cameraAltitudeKm)
		this.sphereRadius.value = wgs.a + this.currentAltitudeKm
		// Wall-clock drives the streak motion so the jet flows even while
		// the scene clock is paused
		this.flowTime.value = (performance.now() / 1000) % 3600
	}
}

export class JetStreamSphereNode extends NodeBase {
	public context: EarthBrowserContext
	private geometry: PressureIsolineSphereGeometry
	private material: JetStreamSphereMaterial
	private sphereRenderer: JetStreamSphereRenderer
	private textureSeries: PressureFieldTextureSeries<JetStreamComputedFrame>

	constructor(context: EarthBrowserContext, resolution: number = 1) {
		super()
		this.context = context

		// Bounds sized for the highest shell altitude; the renderer moves
		// the vertices to the camera-dependent radius each frame
		this.geometry = new PressureIsolineSphereGeometry(context, resolution, cJetShell.maxKm)
		this.material = new JetStreamSphereMaterial()
		this.sphereRenderer = new JetStreamSphereRenderer(context, this.geometry, this.material)
		this.renderer = this.sphereRenderer

		this.textureSeries = new PressureFieldTextureSeries<JetStreamComputedFrame>(context)
		this.textureSeries.debugLabel = "jetstream"
	}

	public setFrames(frames: JetStreamComputedFrame[]): void {
		this.textureSeries.setFrames(frames)
	}

	public clearFrames(): void {
		this.textureSeries.clear()
	}

	/** Free the GPU wind textures while hidden; frames re-fetch when shown */
	public purgeTextures(): void {
		this.textureSeries.purgeAll()
	}

	/** Shell altitude in km for the last rendered frame */
	public get altitudeKm(): number {
		return this.sphereRenderer.currentAltitudeKm
	}

	/** Peak wind speed (m/s) in the frame currently displayed, for the legend */
	public get currentMaxSpeedMps(): number {
		return this.textureSeries.currentFrame?.maxSpeedMps ?? 0
	}

	override renderActions(state: RenderState): RenderActionSet[] {
		const series = this.textureSeries
		series.updateState(state)

		// The jet is a synoptic-scale product: fade it out as the camera
		// descends into regional views where a full-screen flow texture
		// would only obscure the surface
		const cameraAltitudeKm = new geographic(state.camera.position).altitude
		const altitudeFade = Math.min(1, Math.max(0, (cameraAltitudeKm - cFadeOutAltitudeKm) / (cFadeInAltitudeKm - cFadeOutAltitudeKm)))

		this.sphereRenderer.updateTextures(
			series.texture0,
			series.texture1,
			series.blendFactor,
			series.fadeFactor * altitudeFade
		)

		return super.renderActions(state)
	}

	override destroy(): void {
		this.textureSeries.clear()
		this.geometry.destroy()
		super.destroy()
	}
}
